import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import type { ZahlungInfo } from "./types";

/* Panels — identische Reihenfolge wie KFZ:
   Beitrag · Vertragsdaten · Zahlweise · Zahlungsart · Bankverbindung ·
   Rechnungsstellung · Bemerkungen. */

const ZAHLWEISE_OPTIONS = [
  { value: "jaehrlich", label: "jährlich" },
  { value: "halbjaehrlich", label: "halbjährlich" },
  { value: "vierteljaehrlich", label: "vierteljährlich" },
  { value: "monatlich", label: "monatlich - nur mit Einzugsermächtigung" },
];

const ZAHLUNGSART_OPTIONS = [
  { value: "abbuchung_versicherer", label: "Abbuchung durch Versicherer" },
  { value: "abbuchung_courte", label: "Abbuchung durch Courté (nur nach Vereinbarung für Firmenkunden)" },
  { value: "ueberweisung_versicherer", label: "Überweisung an Versicherer" },
  { value: "ueberweisung_courte", label: "Überweisung an Courté" },
];

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", rowGap: "12px" },
  outerCard: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    paddingTop: "18px",
    paddingBottom: "18px",
    paddingLeft: "20px",
    paddingRight: "20px",
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    marginBottom: "4px",
  },
  icon: {
    width: "44px",
    height: "44px",
    flexShrink: 0,
    ...shorthands.borderRadius("999px"),
    color: BRAND.white,
    backgroundColor: BRAND.purple,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.2rem",
    color: BRAND.ink,
    margin: 0,
  },
  subtitle: { fontSize: "0.78rem", color: BRAND.muted },
  subPanel: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    paddingTop: "16px",
    paddingBottom: "16px",
    ...shorthands.borderTop("1px", "dashed", BRAND.borderDashed),
  },
  subPanelFirst: {
    ...shorthands.borderTop("0"),
    paddingTop: "8px",
  },
  subTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.1rem",
    color: BRAND.ink,
    margin: 0,
  },
  rows: { display: "flex", flexDirection: "column", rowGap: "10px" },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr",
    columnGap: "16px",
    rowGap: "6px",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "minmax(220px, 0.9fr) 1.1fr",
      alignItems: "center",
    },
  },
  label: { display: "flex", flexDirection: "column", rowGap: "2px" },
  frage: { fontSize: "0.95rem", color: BRAND.ink, fontWeight: 600 },
  merkmal: {
    fontSize: "0.78rem",
    color: BRAND.muted,
    fontStyle: "italic",
    whiteSpace: "pre-line",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  textarea: { minHeight: "84px", resize: "vertical" },
  select: {
    appearance: "none",
    backgroundImage:
      "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3e%3cpath fill='%235d5664' d='M6 8L0 0h12L6 8z'/%3e%3c/svg%3e\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    paddingRight: "32px",
    cursor: "pointer",
  },
});

type Props = {
  value: ZahlungInfo;
  onChange: (patch: Partial<ZahlungInfo>) => void;
};

const Row = ({
  styles,
  label,
  hint,
  children,
}: {
  styles: ReturnType<typeof useStyles>;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div className={styles.row}>
    <div className={styles.label}>
      <span className={styles.frage}>{label}</span>
      {hint && <span className={styles.merkmal}>{hint}</span>}
    </div>
    <div>{children}</div>
  </div>
);

const ZahlungSection = ({ value, onChange }: Props) => {
  const styles = useStyles();
  const set = (patch: Partial<ZahlungInfo>) => onChange(patch);

  return (
    <div className={styles.root}>
      <div className={styles.outerCard}>
        <div className={styles.header}>
          <span className={styles.icon}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="6" width="16" height="12" rx="2" />
              <path d="M3 10h16" />
              <path d="M7 14h3" />
            </svg>
          </span>
          <div>
            <h3 className={styles.cardTitle}>Vertragsdaten und Zahlung</h3>
            <span className={styles.subtitle}>
              Jahresprämie und Beitragsinformationen oben, anschließend Vertragsdaten,
              Zahlweise, Zahlungsart, Bankverbindung und Bemerkungen.
            </span>
          </div>
        </div>

        {/* Beitrag */}
        <div className={mergeClasses(styles.subPanel, styles.subPanelFirst)}>
          <h4 className={styles.subTitle}>Beitrag Gesamt jährlich</h4>
          <div className={styles.rows}>
            <Row styles={styles} label="Jahresprämie (Brutto)" hint="€">
              <input
                className={styles.input}
                inputMode="decimal"
                placeholder="€"
                value={value.jahrespraemieBrutto}
                onChange={(e) => set({ jahrespraemieBrutto: e.target.value })}
              />
            </Row>
            <Row styles={styles} label="Jahresprämie (Netto)" hint="€">
              <input
                className={styles.input}
                inputMode="decimal"
                placeholder="€"
                value={value.jahrespraemieNetto}
                onChange={(e) => set({ jahrespraemieNetto: e.target.value })}
              />
            </Row>
            <Row styles={styles} label="Versicherungssteuersatz" hint="%">
              <input
                className={styles.input}
                inputMode="decimal"
                placeholder="%"
                value={value.versicherungssteuersatz}
                onChange={(e) => set({ versicherungssteuersatz: e.target.value })}
              />
            </Row>
            <Row styles={styles} label="Prämie je Zahlweise (Brutto)" hint="€">
              <input
                className={styles.input}
                inputMode="decimal"
                placeholder="€"
                value={value.praemieJeZahlweise}
                onChange={(e) => set({ praemieJeZahlweise: e.target.value })}
              />
            </Row>
          </div>
        </div>

        {/* Vertragsdaten */}
        <div className={styles.subPanel}>
          <h4 className={styles.subTitle}>Vertragsdaten</h4>
          <div className={styles.rows}>
            <Row styles={styles} label="Versicherungsbeginn" hint="manuell">
              <input
                className={styles.input}
                type="date"
                value={value.versicherungsbeginn}
                onChange={(e) => set({ versicherungsbeginn: e.target.value })}
              />
            </Row>
            <Row
              styles={styles}
              label="Versicherungsablauf"
              hint={`Vorbelegung 01.01. Folgejahr\nalternativ manuelle Eingabe`}
            >
              <input
                className={styles.input}
                type="date"
                value={value.versicherungsablauf}
                onChange={(e) => set({ versicherungsablauf: e.target.value })}
              />
            </Row>
            <Row
              styles={styles}
              label="Hauptfälligkeitsdatum"
              hint="Vorbelegung 01.01., alternativ manuell"
            >
              <input
                className={styles.input}
                type="text"
                placeholder="Vorbelegung 01.01., alternativ manuell"
                value={value.hauptfaelligkeit}
                onChange={(e) => set({ hauptfaelligkeit: e.target.value })}
              />
            </Row>
            <Row styles={styles} label="Nächste Fälligkeit" hint="Datum">
              <input
                className={styles.input}
                type="text"
                placeholder="Datum"
                value={value.naechsteFaelligkeit}
                onChange={(e) => set({ naechsteFaelligkeit: e.target.value })}
              />
            </Row>
          </div>
        </div>

        {/* Zahlweise */}
        <div className={styles.subPanel}>
          <h4 className={styles.subTitle}>Zahlweise</h4>
          <div className={styles.rows}>
            <Row
              styles={styles}
              label="Zahlweise"
              hint={ZAHLWEISE_OPTIONS.map((o) => o.label).join("\n")}
            >
              <select
                className={mergeClasses(styles.input, styles.select)}
                value={value.zahlweise}
                onChange={(e) => set({ zahlweise: e.target.value })}
              >
                <option value="">— bitte auswählen —</option>
                {ZAHLWEISE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Row>
          </div>
        </div>

        {/* Zahlungsart */}
        <div className={styles.subPanel}>
          <h4 className={styles.subTitle}>Zahlungsart</h4>
          <div className={styles.rows}>
            <Row
              styles={styles}
              label="Zahlungsart"
              hint={ZAHLUNGSART_OPTIONS.map((o) => o.label).join("\n")}
            >
              <select
                className={mergeClasses(styles.input, styles.select)}
                value={value.zahlungsart}
                onChange={(e) => set({ zahlungsart: e.target.value })}
              >
                <option value="">— bitte auswählen —</option>
                {ZAHLUNGSART_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Row>
          </div>
        </div>

        {/* Bankverbindung */}
        <div className={styles.subPanel}>
          <h4 className={styles.subTitle}>Bankverbindung</h4>
          <div className={styles.rows}>
            <Row
              styles={styles}
              label="Bankverbindung"
              hint={`IBAN, BIC und Name der Bank\n(falls bereits vorhanden anzeigen, falls abweichende zusätzliches Eingabefeld)`}
            >
              <textarea
                className={mergeClasses(styles.input, styles.textarea)}
                placeholder={`IBAN, BIC und Name der Bank\n(falls bereits vorhanden anzeigen, falls abweichende zusätzliches Eingabefeld)`}
                value={value.bankverbindung}
                onChange={(e) => set({ bankverbindung: e.target.value })}
              />
            </Row>
          </div>
        </div>

        {/* Rechnungsstellung */}
        <div className={styles.subPanel}>
          <h4 className={styles.subTitle}>Rechnungsstellung</h4>
          <div className={styles.rows}>
            <Row
              styles={styles}
              label="Rechnungsempfänger"
              hint={`Name und Anschrift (falls bereits vorhanden anzeigen, falls abweichende zusätzliches Eingabefeld)`}
            >
              <textarea
                className={mergeClasses(styles.input, styles.textarea)}
                placeholder="Name und Anschrift (falls bereits vorhanden anzeigen, falls abweichende zusätzliches Eingabefeld)"
                value={value.rechnungsempfaenger}
                onChange={(e) => set({ rechnungsempfaenger: e.target.value })}
              />
            </Row>
            <Row
              styles={styles}
              label="Abweichender Rechnungsempfänger"
              hint="Name und Anschrift (falls abweichend)"
            >
              <textarea
                className={mergeClasses(styles.input, styles.textarea)}
                placeholder="Name und Anschrift (falls abweichend)"
                value={value.abweichenderRechnungsempfaenger}
                onChange={(e) =>
                  set({ abweichenderRechnungsempfaenger: e.target.value })
                }
              />
            </Row>
          </div>
        </div>

        {/* Bemerkungen */}
        <div className={styles.subPanel}>
          <h4 className={styles.subTitle}>Bemerkungen</h4>
          <div className={styles.rows}>
            <Row styles={styles} label="Bemerkungsfeld">
              <textarea
                className={mergeClasses(styles.input, styles.textarea)}
                value={value.bemerkung}
                onChange={(e) => set({ bemerkung: e.target.value })}
              />
            </Row>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZahlungSection;
